import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FileText, Play, RefreshCw, Square } from 'lucide-react';
import { T, feldStil, knopfStil } from '../../lib/designTokens';

// Vertragsauszug Bau (api/_lib/vertragAuszug.js): Testlauf eines Deals, Trockenlauf
// ueber den Bestand und das Ablegen.
//
// Der Bestand wird hier Deal fuer Deal abgearbeitet (seit 07.10.2026, Wunsch des
// Nutzers): erst die Liste holen, dann je Deal ein Aufruf. So gibt es einen
// Fortschritt ("12 von 32") und ein Abbrechen, das nach dem laufenden Deal
// anhaelt. Abgelegt wird nur bei Deals, die im letzten Trockenlauf "Bereit" waren.

const STATUS = {
  abgelegt: { text: 'Abgelegt', ton: 'gut' },
  bereit: { text: 'Bereit', ton: 'gut' },
  vorhanden: { text: 'Schon vorhanden', ton: 'leise' },
  pruefen: { text: 'Von Hand prüfen', ton: 'warn' },
  uebersprungen: { text: 'Übersprungen', ton: 'leise' },
  fehler: { text: 'Fehler', ton: 'gefahr' },
};

const TON = {
  gut: { farbe: T.gut, grund: T.gutGrund, rand: T.gutRand },
  warn: { farbe: T.warn, grund: '#FEF3C7', rand: '#FCD34D' },
  gefahr: { farbe: T.gefahr, grund: T.gefahrGrund, rand: T.gefahrRand },
  leise: { farbe: '#64748B', grund: '#F1F5F9', rand: '#E2E8F0' },
};

function pdfSpeichern(base64, dateiname) {
  const bytes = Uint8Array.from(atob(base64), (zeichen) => zeichen.charCodeAt(0));
  const adresse = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const verweis = document.createElement('a');
  verweis.href = adresse;
  verweis.download = dateiname || 'Vertragsauszug.pdf';
  document.body.appendChild(verweis);
  verweis.click();
  verweis.remove();
  setTimeout(() => URL.revokeObjectURL(adresse), 60000);
}

function Marke({ status }) {
  const art = STATUS[status] || { text: status, ton: 'leise' };
  const ton = TON[art.ton];
  return (
    <span style={{
      display: 'inline-block', whiteSpace: 'nowrap', fontSize: 11, fontWeight: 700,
      color: ton.farbe, background: ton.grund, border: `1px solid ${ton.rand}`,
      borderRadius: 999, padding: '2px 8px',
    }}>{art.text}</span>
  );
}

function Zeile({ eintrag }) {
  return (
    <tr style={{ borderTop: '1px solid #F1F5F9' }}>
      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: T.body }}>{eintrag.dealId}</td>
      <td style={{ padding: '6px 8px', color: T.ink }}>{eintrag.titel}</td>
      <td style={{ padding: '6px 8px' }}><Marke status={eintrag.status} /></td>
      <td style={{ padding: '6px 8px', color: T.body }}>{eintrag.grund}</td>
    </tr>
  );
}

function zaehle(eintraege) {
  return eintraege.reduce((summe, e) => ({ ...summe, [e.status]: (summe[e.status] || 0) + 1 }), {});
}

function zusammenfassung(eintraege) {
  const z = zaehle(eintraege);
  return [
    [z.bereit, 'bereit'], [z.abgelegt, 'abgelegt'], [z.vorhanden, 'schon vorhanden'],
    [z.pruefen, 'von Hand prüfen'], [z.uebersprungen, 'übersprungen'], [z.fehler, 'Fehler'],
  ].filter(([zahl]) => zahl).map(([zahl, wort]) => `${zahl} ${wort}`).join(' · ');
}

function Fortschritt({ lauf }) {
  const anteil = lauf.gesamt ? Math.round((lauf.fertig / lauf.gesamt) * 100) : 100;
  const verb = lauf.art === 'trocken' ? 'geprüft' : 'bearbeitet';
  let zustand = '';
  if (lauf.abgebrochen) zustand = ' – abgebrochen';
  else if (lauf.beendet) zustand = ' – fertig';
  // "offenen": Kunden mit gefuelltem Feld zaehlen nicht mit - sie werden gar nicht bearbeitet.
  const was = lauf.art === 'trocken' ? 'offenen Verträgen' : 'Verträgen';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: T.tMeta, color: T.ink, fontWeight: 600 }}>
        <span>
          {lauf.art === 'trocken' ? 'Trockenlauf' : 'Ablegen'}: {lauf.fertig} von {lauf.gesamt} {was} {verb}{zustand}
        </span>
        <span style={{ color: T.body }}>{anteil} %</span>
      </div>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={lauf.gesamt}
        aria-valuenow={lauf.fertig}
        aria-label={`${lauf.fertig} von ${lauf.gesamt} ${verb}`}
        style={{ height: 8, background: '#E2E8F0', borderRadius: 999, overflow: 'hidden' }}
      >
        <div style={{
          width: `${anteil}%`, height: '100%', borderRadius: 999, transition: 'width .3s ease',
          background: lauf.abgebrochen ? '#94A3B8' : '#E07000',
        }} />
      </div>
      {lauf.eintraege.length ? (
        <div style={{ fontSize: T.tMeta, color: T.body }}>{zusammenfassung(lauf.eintraege)}</div>
      ) : null}
    </div>
  );
}

export default function VertragAuszugTest({ apiBase, session, onFertig }) {
  const [dealId, setDealId] = useState('');
  const [laeuft, setLaeuft] = useState('');
  const [einzel, setEinzel] = useState(null);
  const [lauf, setLauf] = useState(null);
  const [trockenErgebnis, setTrockenErgebnis] = useState(null);
  // Kunden, deren Feld schon gefuellt ist - beim Trockenlauf gleich beiseitegelegt.
  const [erledigt, setErledigt] = useState([]);
  const [zielfeldProblem, setZielfeldProblem] = useState('');
  const [fehlerText, setFehlerText] = useState('');
  const [bricktAb, setBrichtAb] = useState(false);
  const abbruch = useRef(false);

  // Verlaesst der Admin die Seite, haelt der Lauf nach dem laufenden Deal an.
  useEffect(() => () => { abbruch.current = true; }, []);

  const senden = (body, timeout) => axios.post(
    `${apiBase}/automation/vertrag-auszug`,
    body,
    { headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}, timeout },
  );

  const testlauf = async (mitPdf) => {
    setLaeuft('test');
    setEinzel(null);
    setFehlerText('');
    try {
      const { data } = await senden({ op: 'test', dealId: dealId.trim(), mitPdf }, 180000);
      setEinzel(data.eintrag);
      if (mitPdf && data.eintrag?.pdfBase64) pdfSpeichern(data.eintrag.pdfBase64, data.eintrag.dateiname);
    } catch (error) {
      setFehlerText(error?.response?.data?.detail || error.message || 'Testlauf fehlgeschlagen.');
    } finally {
      setLaeuft('');
    }
  };

  /** Arbeitet die Deals nacheinander ab; liefert alle Ergebnisse bis zum Ende oder Abbruch. */
  const durchlaufe = async (art, deals) => {
    abbruch.current = false;
    setBrichtAb(false);
    setLauf({ art, gesamt: deals.length, fertig: 0, eintraege: [], abgebrochen: false, beendet: false });
    let eintraege = [];
    for (let i = 0; i < deals.length; i += 1) {
      if (abbruch.current) {
        setLauf((vorher) => ({ ...vorher, abgebrochen: true, beendet: true }));
        return eintraege;
      }
      const deal = deals[i];
      let eintrag;
      if (deal.vorhanden) {
        // Feld schon gefuellt: Kunde ueberspringen, ohne den Server zu fragen.
        eintrag = { ...deal, status: 'vorhanden' };
      } else {
        try {
          // Nacheinander: jeder Aufruf laedt eine PDF aus Bitrix, und Bitrix drosselt.
          // Bei Scans rendert der Server die Seiten und fragt die KI - das dauert.
          const { data } = await senden({
            op: 'deal', dealId: deal.dealId, trocken: art === 'trocken', seite: deal.seite || null,
          }, 180000);
          eintrag = { ...deal, ...data.eintrag };
        } catch (error) {
          eintrag = { ...deal, status: 'fehler', grund: error?.response?.data?.detail || error.message || 'Aufruf fehlgeschlagen.' };
        }
      }
      eintraege = [...eintraege, eintrag];
      const fertig = i + 1;
      const stand = eintraege;
      setLauf((vorher) => ({ ...vorher, fertig, eintraege: stand }));
    }
    setLauf((vorher) => ({ ...vorher, beendet: true }));
    return eintraege;
  };

  const trockenlauf = async () => {
    setLaeuft('trocken');
    setFehlerText('');
    setTrockenErgebnis(null);
    try {
      const { data } = await senden({ op: 'liste' }, 60000);
      setZielfeldProblem(data.zielfeldProblem || '');
      // Feld schon gefuellt: nicht bearbeiten, nicht mitzaehlen, nur nennen
      // (Nutzer, 07.10.2026: "es will wieder alle Kunden machen").
      const alle = data.deals || [];
      setErledigt(alle.filter((d) => d.vorhanden));
      // Auch ein abgebrochener Trockenlauf zaehlt: abgelegt wird dann nur, was schon geprueft ist.
      setTrockenErgebnis(await durchlaufe('trocken', alle.filter((d) => !d.vorhanden)));
    } catch (error) {
      setFehlerText(error?.response?.data?.detail || error.message || 'Trockenlauf fehlgeschlagen.');
    } finally {
      setLaeuft('');
    }
  };

  const bereit = (trockenErgebnis || []).filter((e) => e.status === 'bereit');

  const ablegen = async () => {
    if (!window.confirm(
      `Für ${bereit.length} Deal(s), die im Trockenlauf „Bereit“ waren, wird jetzt der Auszug im Feld „Ernsthaftigkeitsnachweis“ abgelegt. Fortfahren?`,
    )) return;
    setLaeuft('echt');
    setFehlerText('');
    try {
      // Die Seite aus dem Trockenlauf geht mit: Der Server legt nur ab, wenn er
      // wieder genau diese Seite findet.
      const bearbeitet = await durchlaufe('echt', bereit.map(({ dealId: id, titel, phase, seiten }) => ({
        dealId: id, titel, phase, seite: seiten?.unterschrift || null,
      })));
      // Erledigtes faellt aus dem Trockenlauf heraus; nach einem Abbruch bleiben
      // die uebrigen "Bereit" stehen und lassen sich ohne neuen Trockenlauf ablegen.
      const erledigt = new Set(bearbeitet.map((e) => e.dealId));
      setTrockenErgebnis((vorher) => (vorher || []).filter((e) => !erledigt.has(e.dealId)));
      onFertig?.();
    } finally {
      setLaeuft('');
    }
  };

  const abbrechen = () => {
    abbruch.current = true;
    setBrichtAb(true);
  };

  const testBereit = Boolean(dealId.trim()) && !laeuft;
  const bestandLaeuft = laeuft === 'trocken' || laeuft === 'echt';
  const ablegenFrei = bereit.length > 0 && !zielfeldProblem && !laeuft;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: T.tMeta, color: T.body, lineHeight: 1.6 }}>
          <strong style={{ color: T.ink }}>Einzelner Deal.</strong> Sucht die Unterschriftsseite und baut den Auszug –
          ohne etwas in Bitrix abzulegen. „Mit PDF“ lädt den Auszug zum Ansehen herunter.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={dealId}
            onChange={(e) => setDealId(e.target.value)}
            placeholder="Deal-ID, z. B. 11490"
            aria-label="Deal-ID für den Testlauf"
            style={{ ...feldStil, width: 200 }}
          />
          <button type="button" onClick={() => testlauf(false)} disabled={!testBereit}
            style={{ ...knopfStil(true), opacity: testBereit ? 1 : 0.6 }}>
            <RefreshCw size={13} style={laeuft === 'test' ? { animation: 'spin 1s linear infinite' } : undefined} />
            {laeuft === 'test' ? 'Prüft …' : 'Testlauf'}
          </button>
          <button type="button" onClick={() => testlauf(true)} disabled={!testBereit}
            style={{ ...knopfStil(false), opacity: testBereit ? 1 : 0.6 }}>
            <FileText size={13} /> Testlauf mit PDF
          </button>
        </div>
        {einzel ? (
          <div style={{ fontSize: T.tMeta, color: T.body, display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <Marke status={einzel.status} />
            <span>{einzel.titel ? `${einzel.titel}: ` : ''}{einzel.grund}</span>
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid #F1F5F9', paddingTop: 14 }}>
        <div style={{ fontSize: T.tMeta, color: T.body, lineHeight: 1.6 }}>
          <strong style={{ color: T.ink }}>Bestand.</strong> Alle Deals in „Bau“ bis „Netzbetreiber Rückfragen“ mit
          beidseitig unterschriebenem Vertrag. Der Trockenlauf zeigt, was passieren würde; „Ablegen“ legt danach
          nur bei den Deals ab, die dort „Bereit“ waren. Schon gefüllte Felder bleiben unangetastet.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={trockenlauf} disabled={Boolean(laeuft)}
            style={{ ...knopfStil(false), opacity: laeuft ? 0.6 : 1 }}>
            <RefreshCw size={13} style={laeuft === 'trocken' ? { animation: 'spin 1s linear infinite' } : undefined} />
            {laeuft === 'trocken' ? 'Prüft …' : 'Trockenlauf über alle'}
          </button>
          <button type="button" onClick={ablegen} disabled={!ablegenFrei}
            title={ablegenFrei ? '' : 'Erst einen Trockenlauf starten'}
            style={{ ...knopfStil(true), opacity: ablegenFrei ? 1 : 0.5 }}>
            <Play size={13} />
            {laeuft === 'echt' ? 'Legt ab …' : `Ablegen${bereit.length ? ` (${bereit.length})` : ''}`}
          </button>
          {bestandLaeuft ? (
            <button type="button" onClick={abbrechen} disabled={bricktAb}
              style={{ ...knopfStil(false), color: T.gefahr, borderColor: T.gefahrRand, opacity: bricktAb ? 0.6 : 1 }}>
              <Square size={12} />
              {bricktAb ? 'Hält nach diesem Deal an …' : 'Abbrechen'}
            </button>
          ) : null}
        </div>

        {zielfeldProblem ? <div style={{ fontSize: T.tMeta, color: T.gefahr }}>{zielfeldProblem}</div> : null}

        {lauf ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {lauf.art === 'trocken' && erledigt.length ? (
              <details style={{ fontSize: T.tMeta, color: T.body }}>
                <summary style={{ cursor: 'pointer' }}>
                  {erledigt.length} Kunde(n) übersprungen – „Ernsthaftigkeitsnachweis“ ist schon gefüllt
                </summary>
                <div style={{ marginTop: 6, lineHeight: 1.7 }}>
                  {erledigt.map((d) => `${d.dealId} ${d.titel}`).join(' · ')}
                </div>
              </details>
            ) : null}
            <Fortschritt lauf={lauf} />
            {lauf.eintraege.length ? (
              <div style={{ overflowX: 'auto', border: '1px solid #E2E8F0', borderRadius: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#F8FAFC', textAlign: 'left', color: '#64748B' }}>
                      <th style={{ padding: '6px 8px' }}>Deal</th>
                      <th style={{ padding: '6px 8px' }}>Titel</th>
                      <th style={{ padding: '6px 8px' }}>Ergebnis</th>
                      <th style={{ padding: '6px 8px' }}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lauf.eintraege.map((eintrag) => <Zeile key={eintrag.dealId} eintrag={eintrag} />)}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {fehlerText ? <div style={{ fontSize: T.tMeta, color: T.gefahr }}>{fehlerText}</div> : null}
    </div>
  );
}
